import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { Building2, Users, Landmark, SlidersHorizontal, CalendarDays, ChevronLeft, Hash, CreditCard, FileText, Mail, Cpu, Puzzle, KeyRound, Terminal, Timer, Megaphone, FileCode2, BookOpen } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const MENU = [
  { to: '/settings/organisation',          label: 'Organisation',              icon: Building2 },
  { to: '/settings/utilisateurs',          label: 'Utilisateurs',              icon: Users },
  { to: '/settings/societes',              label: 'Sociétés & Agences',        icon: Landmark },
  { to: '/settings/parametres-generaux',   label: 'Paramètres généraux',       icon: SlidersHorizontal },
  { to: '/settings/numerotation',          label: 'Numérotation',              icon: Hash },
  { to: '/settings/jours-feries',          label: 'Jours fériés',              icon: CalendarDays },
  { to: '/settings/conditions-paiement',   label: 'Conditions paiement',       icon: CreditCard },
  { to: '/settings/types-documents',       label: 'Types de documents',        icon: FileText },
  { to: '/settings/types-fichiers',        label: 'Types de fichiers',         icon: FileText },
  { to: '/settings/modeles-emails',        label: 'Modèles d\'emails',         icon: Mail },
  { to: '/settings/comptabilite',          label: 'Comptabilité',              icon: BookOpen },
  { to: '/settings/integrations',          label: 'Intégrations',              icon: Puzzle },
  { to: '/settings/api-keys',              label: 'API',                       icon: KeyRound },
];

const ADMIN_MENU = [
  { to: '/settings/announcements',    label: 'Annonces globales',  icon: Megaphone  },
  { to: '/settings/emails-systeme',   label: 'Emails système',     icon: Mail       },
  { to: '/settings/pdf-generators',   label: 'Générateurs PDF',    icon: FileCode2  },
  { to: '/settings/cron-jobs',        label: 'Cron Jobs',          icon: Timer      },
  { to: '/settings/architecture',     label: 'Architecture app',   icon: Cpu        },
  { to: '/settings/logs',             label: 'Logs serveur',       icon: Terminal   },
];

export default function SettingsLayout() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  return (
    <div className="flex gap-8 items-start">
      {/* Sidebar paramètres */}
      <aside className="w-56 flex-shrink-0">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 mb-6 text-sm font-medium"
          style={{ color: 'var(--text-muted)' }}
          data-testid="settings-back-btn"
        >
          <ChevronLeft size={15} /> Retour
        </button>

        <p className="text-xs font-semibold uppercase tracking-widest mb-3 px-3"
          style={{ color: 'var(--text-disabled)' }}>
          Paramètres
        </p>

        <nav className="flex flex-col gap-0.5">
          {MENU.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`settings-nav-${label.toLowerCase().replace(/[^a-z]/g, '-')}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'text-white'
                    : 'hover:bg-white/60'
                }`
              }
              style={({ isActive }) => isActive
                ? { background: 'var(--primary-gradient)', color: '#fff' }
                : { color: 'var(--text-secondary)' }
              }
            >
              <Icon size={15} />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* Section Super-Admin uniquement */}
        {isSuperAdmin && (
          <>
            <p className="text-xs font-semibold uppercase tracking-widest mt-5 mb-2 px-3"
              style={{ color: 'var(--text-disabled)' }}>
              Administration
            </p>
            <nav className="flex flex-col gap-0.5">
              {ADMIN_MENU.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  data-testid={`settings-nav-${label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive ? 'text-white' : 'hover:bg-white/60'
                    }`
                  }
                  style={({ isActive }) => isActive
                    ? { background: 'linear-gradient(135deg, #1E40AF, #7C3AED)', color: '#fff' }
                    : { color: 'var(--text-secondary)' }
                  }
                >
                  <Icon size={15} />
                  {label}
                </NavLink>
              ))}
            </nav>
          </>
        )}
      </aside>

      {/* Contenu */}
      <div className="flex-1 min-w-0">
        <Outlet />
      </div>
    </div>
  );
}
